/**
 * Order remarks panel — mirrors `fasto_staff` `OrderStaffTeamBox` and
 * `CallAudioBox`. Renders a column of role tabs (Packer, Biller, Rider,
 * Customer) where each tab shows:
 *
 *   - free-form text remarks added by support/staff, and
 *   - support call rows with an inline `<audio>` player when a recording
 *     `file` is attached (MEDIA URL).
 *
 * The data is consumed straight off the OrderDetail payload — see
 * `OrderRemark` in `src/api/legacy/orders.ts`. We do NOT add a new BI
 * endpoint; we just render what `/staff/api/orders/{id}/` already returns.
 */
import {
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  ShieldCheck,
  User as UserIcon,
  Headphones,
  Bike,
  Package as PackageIcon,
  Receipt,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { OrderRemark } from '@/api/legacy/orders';

type RoleKey = 'packer' | 'biller' | 'rider' | 'customer';

const ROLE_META: Record<
  RoleKey,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  packer: { label: 'Packer', icon: PackageIcon },
  biller: { label: 'Biller', icon: Receipt },
  rider: { label: 'Rider', icon: Bike },
  customer: { label: 'Customer', icon: UserIcon },
};

export interface OrderRemarksPanelProps {
  packer?: OrderRemark[] | null;
  biller?: OrderRemark[] | null;
  rider?: OrderRemark[] | null;
  customer?: OrderRemark[] | null;
}

/** Resolve a possibly-relative Django MEDIA path to an absolute URL. */
function resolveMediaUrl(file: string | null | undefined): string | null {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return file;
  // In dev Vite proxies `/media` to Django (see vite.config.ts). In prod
  // we hit the API host directly.
  const base = import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return `${base}${file.startsWith('/') ? '' : '/'}${file}`;
}

function formatBytes(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** True if the row has *any* renderable content. */
function isMeaningful(r: OrderRemark): boolean {
  return Boolean(
    (r.remarks && r.remarks.trim().length > 0) || r.file || r.support || r.phone,
  );
}

export function OrderRemarksPanel({
  packer,
  biller,
  rider,
  customer,
}: OrderRemarksPanelProps) {
  const buckets = useMemo<Record<RoleKey, OrderRemark[]>>(
    () => ({
      packer: (packer ?? []).filter(isMeaningful),
      biller: (biller ?? []).filter(isMeaningful),
      rider: (rider ?? []).filter(isMeaningful),
      customer: (customer ?? []).filter(isMeaningful),
    }),
    [packer, biller, rider, customer],
  );

  const totals: Record<RoleKey, { count: number; calls: number }> = useMemo(
    () => ({
      packer: countTotals(buckets.packer),
      biller: countTotals(buckets.biller),
      rider: countTotals(buckets.rider),
      customer: countTotals(buckets.customer),
    }),
    [buckets],
  );

  const order: RoleKey[] = ['packer', 'biller', 'rider', 'customer'];

  // Default-open the first role that actually has remarks.
  const initial = order.find((r) => totals[r].count > 0) ?? 'packer';
  const [active, setActive] = useState<RoleKey>(initial);

  const grandTotal = order.reduce((sum, r) => sum + totals[r].count, 0);
  const grandCalls = order.reduce((sum, r) => sum + totals[r].calls, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Headphones className="h-4 w-4" /> Remarks & call recordings
        </CardTitle>
        <div className="inline-flex items-center gap-2">
          {grandCalls > 0 ? (
            <Badge tone="info" className="gap-1">
              <PhoneCall className="h-3 w-3" /> {grandCalls} call{grandCalls === 1 ? '' : 's'}
            </Badge>
          ) : null}
          <Badge tone="neutral">{grandTotal} entries</Badge>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div className="flex flex-wrap gap-1 border-b border-border bg-muted/20 px-2 py-1">
          {order.map((r) => {
            const Icon = ROLE_META[r].icon;
            const isActive = active === r;
            const t = totals[r];
            return (
              <button
                key={r}
                type="button"
                onClick={() => setActive(r)}
                className={cn(
                  'group inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors',
                  isActive
                    ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="font-medium">{ROLE_META[r].label}</span>
                {t.count > 0 ? (
                  <span
                    className={cn(
                      'kpi-num inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]',
                      isActive ? 'bg-muted text-foreground' : 'bg-background text-muted-foreground',
                    )}
                  >
                    {t.count}
                    {t.calls > 0 ? (
                      <PhoneCall className="h-2.5 w-2.5 text-info" />
                    ) : null}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="max-h-[420px] overflow-y-auto p-3">
          <RemarksList items={buckets[active]} />
        </div>
      </CardBody>
    </Card>
  );
}

function countTotals(items: OrderRemark[]): { count: number; calls: number } {
  let calls = 0;
  for (const r of items) if (r.file) calls += 1;
  return { count: items.length, calls };
}

function RemarksList({ items }: { items: OrderRemark[] }) {
  // Show newest first — `at` is the upload/created time on the backend.
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const at = a.at ? new Date(a.at).getTime() : 0;
      const bt = b.at ? new Date(b.at).getTime() : 0;
      return bt - at;
    });
  }, [items]);

  if (sorted.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No remarks recorded.
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {sorted.map((r, i) => (
        <li
          key={(r.id != null ? String(r.id) : 'r') + '-' + i}
          className="rounded-md border border-border bg-muted/20 p-3"
        >
          <RemarkRow remark={r} />
        </li>
      ))}
    </ul>
  );
}

function RemarkRow({ remark }: { remark: OrderRemark }) {
  const isCall = !!remark.file;
  const url = resolveMediaUrl(remark.file ?? null);
  const author = remark.support?.name?.trim() || remark.name?.trim() || 'Unknown';
  const when = remark.called_at ?? remark.at ?? null;

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          {isCall ? (
            <span
              className={cn(
                'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                remark.is_outgoing ? 'bg-success/15 text-success' : 'bg-info/15 text-info',
              )}
              title={remark.is_outgoing ? 'Outgoing call' : 'Incoming call'}
            >
              {remark.is_outgoing ? (
                <PhoneOutgoing className="h-3.5 w-3.5" />
              ) : (
                <PhoneIncoming className="h-3.5 w-3.5" />
              )}
            </span>
          ) : (
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
            </span>
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{author}</div>
            <div className="text-[11px] text-muted-foreground">
              {remark.support?.name ? (
                <>
                  via support
                  {remark.name ? (
                    <>
                      {' · '}
                      <span className="text-foreground">{remark.name}</span>
                    </>
                  ) : null}
                </>
              ) : isCall ? (
                'Support call'
              ) : (
                'Note'
              )}
              {remark.phone ? (
                <>
                  {' · '}
                  <a href={`tel:${remark.phone}`} className="hover:underline">
                    {remark.phone}
                  </a>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div className="text-right text-[11px] text-muted-foreground">
          {when ? (
            <>
              <div>{fmt.datetime(when)}</div>
              {remark.duration != null ? <div>{fmt.duration(remark.duration)}</div> : null}
            </>
          ) : (
            '—'
          )}
        </div>
      </div>

      {remark.remarks?.trim() ? (
        <p className="whitespace-pre-wrap text-sm leading-snug text-foreground">
          {remark.remarks}
        </p>
      ) : null}

      {isCall && url ? (
        <div className="rounded-md border border-border/60 bg-background p-2">
          <audio
            controls
            preload="none"
            src={url}
            className="h-9 w-full"
            aria-label={`Call recording from ${author}`}
          >
            <a href={url} download>
              Download recording
            </a>
          </audio>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span>
              {remark.is_outgoing ? 'Outgoing' : 'Incoming'}
              {remark.size != null ? ` · ${formatBytes(remark.size)}` : ''}
              {remark.duration != null ? ` · ${fmt.duration(remark.duration)}` : ''}
            </span>
            <a
              href={url}
              download
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Download
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}
